'use client'

import { useEffect } from 'react'

import '@/lib/logger'

interface GlobalErrorProps {
  error: Error & { digest?: string }
  reset: () => void
}

/**
 * Global Error Boundary Component
 * Catches errors at the application root level
 * Must be a Client Component
 */
export default function GlobalError({ error, reset }: GlobalErrorProps) {
  useEffect(() => {
    // Log error to console
    logger.error('Global Error Boundary caught:', {
      message: error.message,
      digest: error.digest,
      stack: error.stack,
    })
  }, [error])

  return (
    <html>
      <body>
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-red-100">
          <div className="max-w-2xl w-full mx-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
              {/* Error Icon */}
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-10 h-10 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
              </div>

              {/* Error Content */}
              <div className="text-center">
                <h1 className="text-3xl font-bold text-gray-900 mb-4">Something went wrong!</h1>

                <p className="text-lg text-gray-600 mb-8">
                  We apologize for the inconvenience. An unexpected error has occurred. Our team has
                  been notified and is working to fix the issue.
                </p>

                {/* Error Details (Development Only) */}
                {process.env.NODE_ENV === 'development' && (
                  <div className="mb-8 p-4 bg-gray-50 rounded-lg text-left">
                    <p className="text-sm font-mono text-gray-700 break-all">
                      {error.message || 'Unknown error'}
                    </p>
                    {error.digest && (
                      <p className="text-xs text-gray-500 mt-2">Error ID: {error.digest}</p>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={reset}
                    className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-md hover:shadow-lg"
                  >
                    Try Again
                  </button>

                  <button
                    onClick={() => (window.location.href = '/')}
                    className="px-6 py-3 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 transition-colors duration-200"
                  >
                    Go to Home
                  </button>
                </div>

                {/* Support Information */}
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <p className="text-sm text-gray-500">
                    If this problem persists, please contact support
                  </p>
                  <a
                    href="mailto:support@ethpass.gov.et"
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    support@ethpass.gov.et
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </body>
    </html>
  )
}
